import { appErrorOf, type ContractCallContext } from "./contract-client";
import type { AccessScope, CachedAccessToken } from "./regional-token-cache";
import { regionalCall, type RegionalOptions } from "./regional-request";

export type ContractCallOptions = { context: ContractCallContext; signal: AbortSignal };
export type ContractCall<Client, T> = (
  client: Client,
  workspaceId: string,
  options: ContractCallOptions,
  /** The token the call goes out with; its `signal` ends when the scope changes. */
  access: CachedAccessToken,
) => Promise<T>;

/**
 * One call to a contract client through `regionalCall`: the workspace's region and token, the
 * shared bound and 401 retry, and failures mapped to `AppError` with the answer's request ID.
 */
export function contractRequest<Client, T>(
  client: Client,
  scope: AccessScope,
  options: RegionalOptions,
  call: ContractCall<Client, T>,
): Promise<T> {
  return regionalCall(scope, options, async (base, request, access) => {
    let answer: Response | undefined;
    const context: ContractCallContext = {
      base,
      authorization: request.headers.Authorization,
      fetcher: options.fetcher,
      onResponse: (response) => (answer = response),
    };
    try {
      return await call(
        client,
        scope.workspaceId,
        { context, signal: request.init.signal },
        access,
      );
    } catch (error) {
      throw appErrorOf(error, answer);
    }
  });
}
